import { Component, Input, Output, EventEmitter } from '@angular/core';
import { CdkDragDrop, moveItemInArray, transferArrayItem } from '@angular/cdk/drag-drop';
import { FormField, FormLevel } from '../../utils/form-builder.utils';
import { FieldType, currentDraggedFieldType } from '../field-palette/field-palette.component';
import { generateFieldLabel } from '../../utils/form-builder.utils';

@Component({
  selector: 'lib-form-canvas',
  templateUrl: './form-canvas.component.html',
  styleUrls: ['./form-canvas.component.scss'],
})
export class FormCanvasComponent {
  @Input() level!: FormLevel;
  @Input() isRoot = false;
  @Input() selectedField: FormField | null = null;
  @Input() selectedLevelForConfig: FormLevel | null = null;
  @Input() connectedDropLists: string[] = [];

  @Output() fieldAdded = new EventEmitter<{ field: FormField; level: FormLevel }>();
  @Output() fieldRemoved = new EventEmitter<{ field: FormField; level: FormLevel }>();
  @Output() fieldsReordered = new EventEmitter<{ fields: FormField[]; level: FormLevel }>();
  @Output() fieldSelected = new EventEmitter<{ field: FormField; level: FormLevel }>();
  @Output() levelSelected = new EventEmitter<FormLevel>();
  @Output() addNestedLevel = new EventEmitter<FormLevel>();

  isDragOver = false;

  // TrackBy function for ngFor
  trackByFieldId(index: number, field: FormField): string {
    return field.id;
  }

  onDrop(event: CdkDragDrop<FormField[]>): void {
    if (event.previousContainer === event.container) {
      moveItemInArray(this.level.fields, event.previousIndex, event.currentIndex);
      this.fieldsReordered.emit({ fields: this.level.fields, level: this.level });
    } else {
      transferArrayItem(
        event.previousContainer.data,
        event.container.data,
        event.previousIndex,
        event.currentIndex
      );
      this.fieldsReordered.emit({ fields: this.level.fields, level: this.level });
    }
  }

  // Native HTML5 drop - for palette items
  onNativeDragOver(event: DragEvent): void {
    if (!currentDraggedFieldType) return;
    event.preventDefault();
    this.isDragOver = true;
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'copy';
    }
  }

  onNativeDragLeave(event: DragEvent): void {
    this.isDragOver = false;
  }

  onNativeDrop(event: DragEvent): void {
    if (!currentDraggedFieldType) return;
    event.preventDefault();
    this.isDragOver = false;

    const fieldType = currentDraggedFieldType;
    const newField = this.createFieldFromType(fieldType);
    this.level.fields.push(newField);
    this.fieldAdded.emit({ field: newField, level: this.level });
  }

  createFieldFromType(fieldType: FieldType): FormField {
    const baseField: FormField = {
      id: 'field_' + Date.now() + '_' + Math.random().toString(36).substring(2, 9),
      type: fieldType.type,
      label: generateFieldLabel(fieldType.type, this.level.fields),
      required: false,
      validators: [],
    };

    // Set type-specific defaults
    switch (fieldType.type) {
      case 'text':
        baseField.hint = '';
        baseField.maxLength = 255;
        break;
      case 'textarea':
        baseField.hint = '';
        baseField.maxLength = 2000;
        break;
      case 'number':
        baseField.min = 0;
        baseField.max = 999999;
        break;
      case 'select':
        baseField.options = ['Option 1', 'Option 2', 'Option 3'];
        break;
      case 'photo':
        baseField.maxFiles = 5;
        // DOCU-893: Markieren im Foto ist die Normalfunktion, nicht eine Option,
        // die man erst finden muss. Wer es nicht will, schaltet es am Feld ab.
        // Gilt nur für neu eingefügte Felder — Bestandsvorlagen bleiben unberührt.
        baseField.allowAnnotation = true;
        break;
    }

    return baseField;
  }

  selectField(field: FormField): void {
    this.fieldSelected.emit({ field, level: this.level });
  }

  removeField(field: FormField, event: Event): void {
    event.stopPropagation();
    this.fieldRemoved.emit({ field, level: this.level });
  }

  onAddNestedLevel(): void {
    this.addNestedLevel.emit(this.level);
  }

  onSelectLevel(): void {
    this.levelSelected.emit(this.level);
  }

  isThisLevelSelected(): boolean {
    return this.selectedLevelForConfig?.id === this.level?.id;
  }

  isFieldSelected(field: FormField): boolean {
    return this.selectedField?.id === field.id;
  }

  getFieldTypeIcon(type: string): string {
    const iconMap: Record<string, string> = {
      text: 'text_fields',
      textarea: 'notes',
      number: 'pin',
      date: 'calendar_today',
      time: 'schedule',
      select: 'arrow_drop_down_circle',
      checkbox: 'check_box',
      divider: 'horizontal_rule',
      photo: 'photo_camera',
    };
    return iconMap[type] || 'help_outline';
  }
}
